import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeftRight,
  Check,
  X,
  RefreshCw,
  Clock3,
  User,
  MessageSquare,
  Gavel,
  Users,
  Send,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";

type SwapRequest = {
  id: string;
  requesting_driver: string;
  target_driver: string | null;
  shift_id: string;
  target_shift_id: string | null;
  reason: string | null;
  status: "pending" | "approved" | "rejected";
  admin_notes: string | null;
  requested_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  is_open_bid: boolean;
  bid_deadline: string | null;
  selected_bid_id: string | null;
  request_type: "release" | "direct_swap";
};

type SwapBid = {
  id: string;
  swap_request_id: string;
  bidding_driver: string;
  offered_shift_id: string | null;
  note: string | null;
  status: "pending" | "accepted" | "rejected" | "withdrawn";
  created_at: string;
  updated_at: string;
};

type ShiftLookup = {
  id: string;
  driver_name: string;
  shift_date: string;
  start_hhmm: string;
  end_hhmm: string;
  shift_type_label?: string | null;
  shift_type_code?: string | null;
  vehicle_number?: string | null;
};

const formatShift = (shift?: ShiftLookup | null) => {
  if (!shift) return "Shift details unavailable";
  return `${format(new Date(`${shift.shift_date}T00:00:00`), "EEE d MMM")} · ${shift.start_hhmm}-${shift.end_hhmm}${shift.shift_type_label ? ` · ${shift.shift_type_label}` : ""}${shift.vehicle_number ? ` · ${shift.vehicle_number}` : ""}`;
};

// A restricted user (see src/lib/permissions.ts, Settings -> Team Access)
// sees only swap requests where their linked driver(s) are the requester
// or the target -- open bids from other drivers on a visible request
// still show, since that's directly relevant to a shift their own driver
// released. Approve/reject requires edit rights on Roster.
const SwapRequestsPanel: React.FC<{ scopedDriverNames?: string[] | null; canEdit?: boolean }> = ({
  scopedDriverNames = null,
  canEdit = true,
}) => {
  const [requests, setRequests] = useState<SwapRequest[]>([]);
  const [bidsByRequest, setBidsByRequest] = useState<Record<string, SwapBid[]>>({});
  const [shiftMap, setShiftMap] = useState<Record<string, ShiftLookup>>({});
  const [loading, setLoading] = useState(true);
  const [processingKey, setProcessingKey] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<"pending" | "all">("pending");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      let requestQuery = supabase
        .from("roster_swap_requests")
        .select("*")
        .order("requested_at", { ascending: false });

      if (filter === "pending") {
        requestQuery = requestQuery.eq("status", "pending");
      }

      const { data: requestData, error: requestError } = await requestQuery.limit(100);
      if (requestError) throw requestError;

      let typedRequests = (requestData || []) as SwapRequest[];
      if (scopedDriverNames) {
        const scoped = new Set(scopedDriverNames);
        typedRequests = typedRequests.filter(
          (r) => scoped.has(r.requesting_driver) || (r.target_driver && scoped.has(r.target_driver))
        );
      }
      setRequests(typedRequests);

      const requestIds = typedRequests.map((r) => r.id);
      if (requestIds.length === 0) {
        setBidsByRequest({});
        setShiftMap({});
        return;
      }

      const { data: bidData, error: bidError } = await supabase
        .from("roster_swap_bids")
        .select("*")
        .in("swap_request_id", requestIds)
        .order("created_at", { ascending: true });

      if (bidError) throw bidError;

      const typedBids = (bidData || []) as SwapBid[];

      const groupedBids: Record<string, SwapBid[]> = {};
      for (const requestId of requestIds) groupedBids[requestId] = [];
      for (const bid of typedBids) {
        if (!groupedBids[bid.swap_request_id]) groupedBids[bid.swap_request_id] = [];
        groupedBids[bid.swap_request_id].push(bid);
      }
      setBidsByRequest(groupedBids);

      const shiftIds = new Set<string>();
      for (const req of typedRequests) {
        if (req.shift_id) shiftIds.add(req.shift_id);
        if (req.target_shift_id) shiftIds.add(req.target_shift_id);
      }
      for (const bid of typedBids) {
        if (bid.offered_shift_id) shiftIds.add(bid.offered_shift_id);
      }

      if (shiftIds.size > 0) {
        const { data: shiftData, error: shiftError } = await supabase
          .from("vw_roster_shifts_enriched")
          .select("id, driver_name, shift_date, start_hhmm, end_hhmm, shift_type_label, shift_type_code, vehicle_number")
          .in("id", Array.from(shiftIds));

        if (shiftError) throw shiftError;

        const nextShiftMap: Record<string, ShiftLookup> = {};
        for (const shift of (shiftData || []) as ShiftLookup[]) {
          nextShiftMap[shift.id] = shift;
        }
        setShiftMap(nextShiftMap);
      } else {
        setShiftMap({});
      }
    } catch (err: any) {
      console.error("Failed to fetch swap requests:", err);
      toast({
        title: "Error",
        description: "Failed to load swap requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [filter, scopedDriverNames]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleResolveOldStyleRequest = async (
    requestId: string,
    newStatus: "approved" | "rejected"
  ) => {
    if (!canEdit) return;
    setProcessingKey(`request-${requestId}-${newStatus}`);
    try {
      const request = requests.find((r) => r.id === requestId);
      if (!request) return;

      const note = adminNotes[requestId] || null;

      const { error: updateError } = await supabase
        .from("roster_swap_requests")
        .update({
          status: newStatus,
          admin_notes: note,
          resolved_at: new Date().toISOString(),
          resolved_by: "admin",
          updated_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      if (updateError) throw updateError;

      if (newStatus === "approved" && request.shift_id && request.target_shift_id) {
        const [shift1Res, shift2Res] = await Promise.all([
          supabase.from("roster_shifts").select("driver_name").eq("id", request.shift_id).single(),
          supabase.from("roster_shifts").select("driver_name").eq("id", request.target_shift_id).single(),
        ]);

        if (shift1Res.error) throw shift1Res.error;
        if (shift2Res.error) throw shift2Res.error;

        if (shift1Res.data && shift2Res.data) {
          await Promise.all([
            supabase
              .from("roster_shifts")
              .update({
                driver_name: shift2Res.data.driver_name,
                updated_at: new Date().toISOString(),
              })
              .eq("id", request.shift_id),

            supabase
              .from("roster_shifts")
              .update({
                driver_name: shift1Res.data.driver_name,
                updated_at: new Date().toISOString(),
              })
              .eq("id", request.target_shift_id),
          ]);
        }
      }

      toast({
        title: newStatus === "approved" ? "Approved" : "Rejected",
        description:
          newStatus === "approved"
            ? "Direct swap approved and shifts updated."
            : "Swap request rejected.",
      });

      fetchRequests();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to process request",
        variant: "destructive",
      });
    } finally {
      setProcessingKey(null);
    }
  };

  const handleAcceptBid = async (requestId: string, bidId: string) => {
    if (!canEdit) return;
    setProcessingKey(`bid-${bidId}-accept`);
    try {
      const request = requests.find((r) => r.id === requestId);
      if (!request) return;

      const bids = bidsByRequest[requestId] || [];
      const winningBid = bids.find((b) => b.id === bidId);
      if (!winningBid) return;

      const note = adminNotes[requestId] || null;

      const { error: shiftUpdateError } = await supabase
        .from("roster_shifts")
        .update({
          driver_name: winningBid.bidding_driver,
          updated_at: new Date().toISOString(),
        })
        .eq("id", request.shift_id);

      if (shiftUpdateError) throw shiftUpdateError;

      const { error: acceptBidError } = await supabase
        .from("roster_swap_bids")
        .update({
          status: "accepted",
          updated_at: new Date().toISOString(),
        })
        .eq("id", bidId);

      if (acceptBidError) throw acceptBidError;

      const otherBidIds = bids
        .filter((b) => b.id !== bidId && b.status === "pending")
        .map((b) => b.id);

      if (otherBidIds.length > 0) {
        const { error: rejectOthersError } = await supabase
          .from("roster_swap_bids")
          .update({
            status: "rejected",
            updated_at: new Date().toISOString(),
          })
          .in("id", otherBidIds);

        if (rejectOthersError) throw rejectOthersError;
      }

      const { error: requestUpdateError } = await supabase
        .from("roster_swap_requests")
        .update({
          status: "approved",
          selected_bid_id: bidId,
          admin_notes: note,
          resolved_at: new Date().toISOString(),
          resolved_by: "admin",
          updated_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      if (requestUpdateError) throw requestUpdateError;

      toast({
        title: "Bid accepted",
        description: `Shift reassigned to ${winningBid.bidding_driver}.`,
      });

      fetchRequests();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to accept bid",
        variant: "destructive",
      });
    } finally {
      setProcessingKey(null);
    }
  };

  const handleRejectBid = async (bidId: string) => {
    if (!canEdit) return;
    setProcessingKey(`bid-${bidId}-reject`);
    try {
      const { error } = await supabase
        .from("roster_swap_bids")
        .update({
          status: "rejected",
          updated_at: new Date().toISOString(),
        })
        .eq("id", bidId);

      if (error) throw error;

      toast({
        title: "Bid rejected",
        description: "The bid has been rejected.",
      });

      fetchRequests();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to reject bid",
        variant: "destructive",
      });
    } finally {
      setProcessingKey(null);
    }
  };

  const pendingCount = useMemo(
    () => requests.filter((r) => r.status === "pending").length,
    [requests]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-purple-400" />
            Swap Requests
          </h3>
          {pendingCount > 0 && (
            <Badge className="bg-purple-600 text-white">{pendingCount} pending</Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-800 border border-gray-700 rounded-md overflow-hidden">
            <button
              onClick={() => setFilter("pending")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                filter === "pending" ? "bg-purple-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                filter === "all" ? "bg-purple-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              All
            </button>
          </div>

          <Button variant="ghost" size="sm" onClick={fetchRequests} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-purple-400" />
          <span className="ml-2 text-gray-400">Loading requests...</span>
        </div>
      ) : requests.length === 0 ? (
        <Card className="bg-gray-800/40 border-gray-700">
          <CardContent className="p-12 text-center">
            <ArrowLeftRight className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No {filter === "pending" ? "pending " : ""}swap requests</p>
            <p className="text-xs text-gray-500 mt-1">
              Once drivers start tossing shifts into the ring, they’ll show up here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => {
            const isPending = req.status === "pending";
            const bids = bidsByRequest[req.id] || [];
            const sourceShift = shiftMap[req.shift_id];
            const targetShift = req.target_shift_id ? shiftMap[req.target_shift_id] : null;
            const selectedBid = req.selected_bid_id ? bids.find((b) => b.id === req.selected_bid_id) : null;

            return (
              <Card
                key={req.id}
                className={`border ${
                  isPending
                    ? "border-purple-700/50 bg-purple-950/20"
                    : req.status === "approved"
                    ? "border-green-700/30 bg-green-950/10"
                    : "border-red-700/30 bg-red-950/10"
                }`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      {req.is_open_bid ? (
                        <Gavel className="h-4 w-4 text-amber-400" />
                      ) : (
                        <ArrowLeftRight className="h-4 w-4 text-purple-400" />
                      )}
                      <span>
                        {req.is_open_bid ? "Open Bid Request" : req.request_type === "direct_swap" ? "Direct Swap" : "Shift Release"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] border-gray-600 text-gray-300">
                        {req.request_type}
                      </Badge>
                      <Badge
                        variant={
                          req.status === "approved"
                            ? "default"
                            : req.status === "rejected"
                            ? "destructive"
                            : "secondary"
                        }
                        className="text-[10px]"
                      >
                        {req.status}
                      </Badge>
                    </div>
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <User className="h-4 w-4 text-blue-400" />
                          <span className="text-sm font-medium text-white">{req.requesting_driver}</span>
                        </div>

                        <ArrowLeftRight className="h-4 w-4 text-gray-500" />

                        <div className="flex items-center gap-1.5">
                          <User className="h-4 w-4 text-cyan-400" />
                          <span className="text-sm font-medium text-white">
                            {req.target_driver || (req.is_open_bid ? "Open to bids" : "Any driver")}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        <div className="rounded-md border border-gray-700 bg-gray-900/50 p-3">
                          <div className="text-[11px] text-gray-400 mb-1">Requested shift</div>
                          <div className="text-sm text-white">{formatShift(sourceShift)}</div>
                        </div>

                        {(req.request_type === "direct_swap" || targetShift) && (
                          <div className="rounded-md border border-gray-700 bg-gray-900/50 p-3">
                            <div className="text-[11px] text-gray-400 mb-1">Target shift</div>
                            <div className="text-sm text-white">{formatShift(targetShift)}</div>
                          </div>
                        )}
                      </div>

                      {req.reason && (
                        <div className="flex items-start gap-1.5">
                          <MessageSquare className="h-3.5 w-3.5 text-gray-500 mt-0.5 shrink-0" />
                          <p className="text-xs text-gray-300">{req.reason}</p>
                        </div>
                      )}

                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Clock3 className="h-3 w-3 text-gray-500" />
                        <span className="text-[11px] text-gray-500">
                          Requested {format(new Date(req.requested_at), "d MMM yyyy 'at' h:mm a")}
                        </span>
                        {req.bid_deadline && (
                          <span className="text-[11px] text-amber-400">
                            · bids close {format(new Date(req.bid_deadline), "d MMM yyyy 'at' h:mm a")}
                          </span>
                        )}
                      </div>

                      {isPending && (
                        <Textarea
                          value={adminNotes[req.id] || ""}
                          onChange={(e) =>
                            setAdminNotes((prev) => ({ ...prev, [req.id]: e.target.value }))
                          }
                          className="bg-gray-800 border-gray-600 text-white text-xs mt-2"
                          rows={2}
                          placeholder="Admin notes (optional)..."
                        />
                      )}

                      {!isPending && (
                        <div className="text-[11px] text-gray-500">
                          {req.status === "approved" ? "Approved" : "Rejected"}
                          {req.resolved_at && ` on ${format(new Date(req.resolved_at), "d MMM yyyy")}`}
                          {selectedBid && ` — winning bidder: ${selectedBid.bidding_driver}`}
                          {req.admin_notes && ` — ${req.admin_notes}`}
                        </div>
                      )}
                    </div>

                    {!req.is_open_bid && isPending && canEdit && (
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          onClick={() => handleResolveOldStyleRequest(req.id, "approved")}
                          disabled={processingKey !== null}
                          className="bg-green-600 hover:bg-green-700 text-white h-8"
                        >
                          {processingKey === `request-${req.id}-approved` ? (
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <>
                              <Check className="h-3.5 w-3.5 mr-1" /> Approve
                            </>
                          )}
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResolveOldStyleRequest(req.id, "rejected")}
                          disabled={processingKey !== null}
                          className="border-red-700 text-red-400 hover:bg-red-900/30 h-8"
                        >
                          <X className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>

                  {req.is_open_bid && (
                    <div className="rounded-lg border border-gray-700/60 bg-gray-900/35 p-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-amber-400" />
                          <span className="text-sm font-medium text-white">
                            Bids ({bids.length})
                          </span>
                        </div>
                      </div>

                      {bids.length === 0 ? (
                        <div className="text-xs text-gray-500 py-3">
                          No bids yet. The tumbleweeds are still warming up.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {bids.map((bid) => {
                            const offeredShift = bid.offered_shift_id ? shiftMap[bid.offered_shift_id] : null;
                            const bidPending = bid.status === "pending";

                            return (
                              <div
                                key={bid.id}
                                className={`rounded-md border p-3 ${
                                  bid.status === "accepted"
                                    ? "border-green-700/40 bg-green-950/10"
                                    : bid.status === "rejected"
                                    ? "border-red-700/40 bg-red-950/10"
                                    : bid.status === "withdrawn"
                                    ? "border-gray-700/40 bg-gray-900/40 opacity-70"
                                    : "border-gray-700 bg-gray-800/50"
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="space-y-2 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <div className="flex items-center gap-1.5">
                                        <Send className="h-3.5 w-3.5 text-cyan-400" />
                                        <span className="text-sm font-medium text-white">
                                          {bid.bidding_driver}
                                        </span>
                                      </div>

                                      <Badge
                                        variant="outline"
                                        className={`text-[10px] ${
                                          bid.status === "accepted"
                                            ? "border-green-500 text-green-400"
                                            : bid.status === "rejected"
                                            ? "border-red-500 text-red-400"
                                            : bid.status === "withdrawn"
                                            ? "border-gray-500 text-gray-400"
                                            : "border-amber-500 text-amber-400"
                                        }`}
                                      >
                                        {bid.status}
                                      </Badge>
                                    </div>

                                    <div className="text-[11px] text-gray-500">
                                      Bid placed {format(new Date(bid.created_at), "d MMM yyyy 'at' h:mm a")}
                                    </div>

                                    {offeredShift && (
                                      <div className="rounded-md border border-gray-700 bg-gray-900/60 p-2 text-xs text-gray-300">
                                        Offered shift: {formatShift(offeredShift)}
                                      </div>
                                    )}

                                    {bid.note && (
                                      <div className="text-xs text-gray-300">
                                        <span className="text-gray-500">Note:</span> {bid.note}
                                      </div>
                                    )}
                                  </div>

                                  {isPending && bidPending && canEdit && (
                                    <div className="flex items-center gap-2 shrink-0">
                                      <Button
                                        size="sm"
                                        onClick={() => handleAcceptBid(req.id, bid.id)}
                                        disabled={processingKey !== null}
                                        className="bg-green-600 hover:bg-green-700 text-white h-8"
                                      >
                                        {processingKey === `bid-${bid.id}-accept` ? (
                                          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                          <>
                                            <Check className="h-3.5 w-3.5 mr-1" />
                                            Accept
                                          </>
                                        )}
                                      </Button>

                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleRejectBid(bid.id)}
                                        disabled={processingKey !== null}
                                        className="border-red-700 text-red-400 hover:bg-red-900/30 h-8"
                                      >
                                        <X className="h-3.5 w-3.5 mr-1" />
                                        Reject
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SwapRequestsPanel;